import { supabase } from '../supabase';
import { config } from '../config';
import type { ConsentLog, ConsentLogInsert } from '../types';

/**
 * Create consent log
 */
export const createConsentLog = async (consent: ConsentLogInsert): Promise<ConsentLog> => {
  // 🔧 DEVELOPMENT MODE - Return mock consent log without database insert
  if (config.devMode.enabled && config.devMode.mockAuth) {
    console.log('🔧 DEV MODE: Skipping consent log database insert (mock mode)');
    console.log('🔧 DEV MODE: Consent would be recorded:', {
      user_id: consent.user_id,
      consent_version: consent.consent_version
    });
    
    // Store consent flag in sessionStorage so getProfile can return it
    sessionStorage.setItem(`consent_given_${consent.user_id}`, 'true');
    console.log('🔧 DEV MODE: Consent flag stored in sessionStorage');
    
    // Return a mock consent log matching the ConsentLog type
    return {
      id: `mock-consent-${Date.now()}`,
      user_id: consent.user_id,
      consent_version: consent.consent_version,
      consent_text: consent.consent_text || '',
      created_at: new Date().toISOString(),
    } as ConsentLog;
  }

  const { data, error } = await supabase
    .from('consent_logs')
    .insert(consent)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Get user's consent logs
 */
export const getUserConsentLogs = async (userId: string): Promise<ConsentLog[]> => {
  // 🔧 DEVELOPMENT MODE - Return mock consent logs
  if (config.devMode.enabled && config.devMode.mockData) {
    console.log('🔧 DEV MODE: Returning mock consent logs');
    return [
      {
        id: 'consent-1',
        user_id: userId,
        consent_version: '1.0',
        consent_text: CONSENT_TEXT,
        created_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
      },
    ] as ConsentLog[];
  }
  
  const { data, error } = await supabase
    .from('consent_logs')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

/**
 * Get user's latest consent
 */
export const getLatestConsent = async (userId: string): Promise<ConsentLog | null> => {
  const { data, error } = await supabase
    .from('consent_logs')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(1)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }

  return data;
};

/**
 * Check if user has consented
 */
export const hasConsented = async (userId: string): Promise<boolean> => {
  const { data, error } = await supabase
    .from('consent_logs')
    .select('id')
    .eq('user_id', userId)
    .limit(1)
    .single();

  if (error && error.code !== 'PGRST116') throw error;
  return !!data;
};

/**
 * Get consent by version (for displaying terms)
 */
export const getConsentByVersion = async (version: string): Promise<ConsentLog | null> => {
  const { data, error } = await supabase
    .from('consent_logs')
    .select('*')
    .eq('consent_version', version)
    .limit(1)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }

  return data;
};

// Alias for getUserConsentLogs for consistency
export const getConsentLogs = getUserConsentLogs;

// Current consent version
export const CURRENT_CONSENT_VERSION = '1.0';

// Consent text template
export const CONSENT_TEXT = `
CannaBias Terms of Service and Privacy Policy

Version ${CURRENT_CONSENT_VERSION}
Last Updated: October 10, 2025

By using CannaBias, you agree to:

1. AGE VERIFICATION
   - You are at least 21 years old (or legal age in your jurisdiction)
   - You have legal authorization to access cannabis-related information

2. DATA PROCESSING
   - We collect and process your personal data as described in our Privacy Policy
   - Your reviews are pseudonymized but may be visible to other users
   - We align with UK GDPR and Cyber Essentials standards

3. REVIEW GUIDELINES
   - Reviews must be based on personal experience
   - No specific dosage recommendations
   - No unsubstantiated medical claims
   - Focus on quality, consistency, packaging, and availability

4. PRIVACY & DATA RIGHTS
   - Right to access your data
   - Right to data portability (export)
   - Right to erasure (delete account)
   - Right to withdraw consent

5. DISCLAIMER
   - Platform does NOT provide medical advice
   - Platform does NOT sell cannabis products
   - Consult healthcare providers before use

6. MODERATION
   - All reviews are subject to moderation
   - Reviews with prohibited content will be removed
   - Appeals process available

For full terms, visit: cannabias.tech/terms
For privacy policy, visit: cannabias.tech/privacy

I have read and agree to these terms.
`.trim();
